"use client";
import useTranslate from "@app/_hooks/useTranslate";
/* eslint-disable @next/next/no-img-element */
import { Heading, Paragraph } from "@app/_components/ui";
import { useMarket } from "@app/_context";
import { type ChangeEvent } from "react";

import {
  isAdyenCheckoutEnabled,
  isWalleyCheckoutEnabled,
} from "@/config/checkout";
import { cn } from "@/lib/utils";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import { useCheckoutZipCode } from "../Checkout.hooks";
import { checkoutStore } from "../Checkout.store";
import { useSetCheckoutDelivery } from "./Delivery.hooks";
import type { DeliveryOption, DeliveryOptionImage } from "./Delivery.utils";
import DeliveryAgents from "./DeliveryAgents";

type Props = {
  option: DeliveryOption;
  image?: DeliveryOptionImage;
  disabled?: boolean;
  onSelect?: () => void;
};

export default function DeliveryOption({
  option,
  image,
  disabled,
  onSelect,
}: Props) {
  const t = useTranslate();

  const zipCode = useCheckoutZipCode();
  const marketConfig = useMarket().state.market;
  const { mutateAsync } = useSetCheckoutDelivery();
  const deliveryOption = checkoutStore((state) => state.deliveryOption);
  const setDeliveryOption = checkoutStore((state) => state.setDeliveryOption);
  const onChange = async () => {
    const cartCookie = getCartCookie();
    if (isAdyenCheckoutEnabled({ marketConfig })) {
      // do stuff
    } else if (isWalleyCheckoutEnabled({ marketConfig })) {
      window.walley?.checkout?.api?.suspend?.();
    } else {
      // do stuff
    }
    return mutateAsync({
      locale: marketConfig.serviceLocale,
      zipCode: zipCode ?? "",
      cartId: cartCookie?.value ?? "",
      deliveryOptionName: option.name,
      deliveryOptionId: option.id,
      deliveryAgentId: option.agent,
      priceValue: option.price,
    }).then(() => {
      setDeliveryOption(option);
      onSelect?.();
    });
  };

  const onChangeAgent = (event: ChangeEvent<HTMLSelectElement>) => {
    const agent = event.target.value;
    if (agent) setDeliveryOption({ ...option, agent });
  };

  return (
    <label
      key={option.id}
      htmlFor={option.id}
      data-testid="DeliveryOption"
      aria-selected={option.id === deliveryOption?.id}
      className="relative flex cursor-pointer gap-4 p-6 transition-colors hover:bg-gray-50 aria-selected:bg-gray-50 max-md:p-3"
      data-id={option.id}
    >
      <input
        disabled={disabled}
        type="radio"
        id={option.id}
        onChange={onChange}
        name="deliveryOptions"
        value={option.id}
        checked={option.id === deliveryOption?.id}
        className="mt-2 self-start"
      />
      <div className="flex grow flex-col">
        <Heading as="h3" size={5} className="mb-2">
          {option.name}
        </Heading>
        <Paragraph size="small">{option.description}</Paragraph>
        <Paragraph className="mt-2" size="xsmall">
          {option.extraDescription}
        </Paragraph>
        {deliveryOption?.id === option.id && (
          <DeliveryAgents onChange={onChangeAgent} agents={option.agents} />
        )}
      </div>
      <div className="flex h-full flex-col items-end justify-start gap-2 md:gap-0">
        <Paragraph>{option.price ? option.prettyPrice : t("free")}</Paragraph>
        {image?.url ? (
          <div
            className={cn([
              "border-gray-150 bg-beige flex aspect-square h-12 w-12 items-center justify-center",
            ])}
          >
            <img
              className="object-contain"
              src={image.url}
              alt={image?.title ?? option.name}
              width={40}
              height={40}
            />
          </div>
        ) : (
          <div className="size-12" />
        )}
      </div>
    </label>
  );
}
